import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

interface TechnicalAnalysisProps {
  data: any;
  timeRange?: string;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

export function TechnicalAnalysis({ data }: TechnicalAnalysisProps) {
  if (!data) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">No technical data available</p>
        </CardContent>
      </Card>
    );
  }

  const shotTypesData = Object.entries(data.shotTypes).map(([name, value]) => ({
    name,
    value
  }));

  const qualityData = Object.entries(data.qualityDistribution).map(([name, value]) => ({
    name,
    value
  }));

  return (
    <div className="space-y-6">
      <div className="grid gap-6 md:grid-cols-2">
        {/* Shot Types Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Shot Types Distribution</CardTitle>
            <CardDescription>
              Breakdown of shots by type
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={shotTypesData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {shotTypesData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Quality Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Shot Quality Distribution</CardTitle>
            <CardDescription>
              Breakdown of shots by quality rating
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={qualityData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip 
                  formatter={(value: number) => [`${value}%`, 'Percentage']}
                />
                <Bar 
                  dataKey="value" 
                  fill="hsl(var(--primary))"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Shot Type Performance */}
      <Card>
        <CardHeader>
          <CardTitle>Shot Type Performance</CardTitle>
          <CardDescription>
            Average success rate by shot type
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={shotTypesData.map(shot => ({
              ...shot,
              successRate: Math.floor(Math.random() * 30) + 60 // Mock success rates
            }))}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip 
                formatter={(value: number) => [`${value}%`, 'Success Rate']}
              />
              <Bar 
                dataKey="successRate" 
                fill="hsl(var(--secondary))"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Technical Insights */}
      <Card>
        <CardHeader>
          <CardTitle>Technical Insights</CardTitle>
          <CardDescription>
            Key technical findings and recommendations
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-medium text-blue-900">Most Popular Shot</h4>
              <p className="text-sm text-blue-700">
                Drive shots account for {data.shotTypes['Drive']}% of all shots, showing good fundamentals
              </p>
            </div>
            
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <h4 className="font-medium text-green-900">Quality Trend</h4>
              <p className="text-sm text-green-700">
                {data.qualityDistribution['Excellent'] + data.qualityDistribution['Good']}% of shots are rated Good or Excellent
              </p>
            </div>
            
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <h4 className="font-medium text-yellow-900">Area for Improvement</h4>
              <p className="text-sm text-yellow-700">
                Focus on {Object.entries(data.shotTypes).sort(([,a], [,b]) => (a as number) - (b as number))[0][0]} shots - currently the least practiced
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}